import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthBootstrap } from "../context/AuthBootstrapContext";
import GlobalHeader from "../pages/components/GlobalHeader";
import NeighborhoodConfirmModal from "../pages/components/NeighborhoodConfirmModal";
import CoffeeFrequencyModal from "../pages/components/CoffeeFrequencyModal";

function FullPageMessage({ text = "Loading your account..." }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background: "#F5F2E8",
      }}
    >
      <p>{text}</p>
    </div>
  );
}

export default function ProtectedAppShell() {
  const location = useLocation();
  const {
    loading,
    authed,
    onboardingRequired,
    onboardingCompleted,
    profileAssets,
    person,
    verification,
    refresh,
  } = useAuthBootstrap();

  const [showNbhdModal, setShowNbhdModal] = useState(false);
  const [showCoffeeFreqModal, setShowCoffeeFreqModal] = useState(false);

  const isOnboardingRoute = location.pathname.startsWith("/onboarding");

  const hasProfileAssetsRow = !!profileAssets;
  const needsOnboarding =
    onboardingRequired === true ||
    !hasProfileAssetsRow ||
    onboardingCompleted !== true;

  const isEligibleForNbhdModal = useMemo(() => {
    const personStatus = String(person?.status || "").toUpperCase();
    const subStatus = String(
      verification?.subscription_status || ""
    ).toLowerCase();
    return (
      ["free", "trialing", "active"].includes(subStatus) ||
      ["PAYMENT_COMPLETE", "APPLICATION_COMPLETE"].includes(personStatus)
    );
  }, [person, verification]);

  useEffect(() => {
    if (loading || !authed) return;
    if (isOnboardingRoute || needsOnboarding) return;
    if (!isEligibleForNbhdModal) return;
    if (String(person?.neighborhood || "").trim()) return;
    if (!String(person?.zip_code || "").trim()) return;
    if (sessionStorage.getItem("fika_nbhd_seen")) return;
    setShowNbhdModal(true);
  }, [
    loading,
    authed,
    isOnboardingRoute,
    needsOnboarding,
    isEligibleForNbhdModal,
    person,
  ]);

  useEffect(() => {
    if (loading || !authed) return;
    if (isOnboardingRoute || needsOnboarding) return;
    if (!isEligibleForNbhdModal) return;
    if (profileAssets?.coffee_frequency) {
      setShowCoffeeFreqModal(false);
      return;
    }
    if (sessionStorage.getItem("fika_coffee_freq_seen")) return;
    setShowCoffeeFreqModal(true);
  }, [
    loading,
    authed,
    isOnboardingRoute,
    needsOnboarding,
    isEligibleForNbhdModal,
    profileAssets,
  ]);

  if (loading) {
    return <FullPageMessage text="Loading your account..." />;
  }

  if (!authed) {
    return <Navigate to="/sign-in" replace state={{ from: location }} />;
  }

  if (needsOnboarding && !isOnboardingRoute) {
    return <Navigate to="/onboarding" replace state={{ from: location }} />;
  }

  if (!needsOnboarding && isOnboardingRoute) {
    const isNYC = String(person?.city || "").toLowerCase().includes("new york");
    return <Navigate to={isNYC ? "/fikas" : "/featured"} replace />;
  }

  if (isOnboardingRoute) {
    return <Outlet />;
  }

  return (
    <>
      <GlobalHeader />
      <Outlet />
      {showNbhdModal && (
        <NeighborhoodConfirmModal
          person={person}
          onClose={() => {
            sessionStorage.setItem("fika_nbhd_seen", "1");
            setShowNbhdModal(false);
          }}
          onSaved={async () => {
            sessionStorage.setItem("fika_nbhd_seen", "1");
            setShowNbhdModal(false);
            if (typeof refresh === "function") await refresh();
          }}
        />
      )}
      {showCoffeeFreqModal && (
        <CoffeeFrequencyModal
          onClose={() => {
            sessionStorage.setItem("fika_coffee_freq_seen", "1");
            setShowCoffeeFreqModal(false);
          }}
          onSaved={async () => {
            sessionStorage.setItem("fika_coffee_freq_seen", "1");
            setShowCoffeeFreqModal(false);
            if (typeof refresh === "function") await refresh();
          }}
        />
      )}
    </>
  );
}
